/* eslint-disable camelcase */
import axios from 'axios';
import { useRef, useState } from 'react';

import BattleRecord from '~/components/war/battle/BattleRecord';
import Warrior from '~/components/war/warrior/Warrior';
import { useGameAuthentication } from '~/contextProviders/GameAuthenticationProvider';
import { useGameContext } from '~/contextProviders/GameContextProvider';
import { useGetWarrior } from '~/hooks/game/useGetWarrior';
import { useGetWarriorAttack } from '~/hooks/game/useGetWarriorAttack';
import { useGetAccount } from '~/hooks/web3/useGetAccount';
import { isLifeAndDeathRound } from '~/utils/utils';

import WarriorControls from '../warrior/WarriorControls';

const generateHiddenURL = (battle, warrior, pair) => {
  const syndicate = pair.warrior_json.attributes.find(
    ({ trait_type }) => trait_type === 'Syndicate'
  )?.value;
  const hp = pair.warrior_json.attributes.find(
    ({ trait_type }) => trait_type === 'HP'
  )?.value;
  const hiddenURL = `/api/matchup/previewHidden?round=${battle}&warriorOneId=${warrior.warrior_id}&syndicate=${syndicate}&hp=${hp}`;
  return hiddenURL;
};

export default function BattleMatchup({
  ownerWarriorId,
  opponentWarriorId,
}: {
  ownerWarriorId: number;
  opponentWarriorId: number;
}) {
  const [isActive, setIsActive] = useState(false);
  const [showOwnerRecord, setShowOwnerRecord] = useState(false);
  const [showOpponentRecord, setShowOpponentRecord] = useState(false);

  const [attack, setAttack] = useState<string>();
  const [shareClicked, setShareClicked] = useState(false);
  const [shareURL, setShareURL] = useState('');

  const shareRef = useRef<HTMLAnchorElement>(null);
  const { areWaitingForResults, battle, contextId, areResultsReady } =
    useGameContext();
  const lifeAndDeathRound = isLifeAndDeathRound(battle);
  const { data: warrior } = useGetWarrior(ownerWarriorId);
  const { data: opponent } = useGetWarrior(opponentWarriorId);
  const { data: connectedAccount } = useGetAccount();
  const { accessToken } = useGameAuthentication();

  const { data: warriorAttackApi, isSuccess: isAttackSuccess } =
    useGetWarriorAttack({
      data: {
        contextId: contextId,
        battleId: battle,
        warriorId: warrior?.warrior_id || 0,
        token: accessToken || '',
      },
      enabled: !!accessToken && !!warrior?.warrior_id,
    });

  if (!warrior || !opponent) {
    return (
      <div className="battle-matchup">
        <div className="battle-matchup__body battle-matchup__body--loading">
          <div className="battle-matchup__revleal-result">
            Loading your matchup...
          </div>
        </div>
      </div>
    );
  }

  // set warrior.owner address of current user to allow ens lookup at low level component
  warrior.owner = connectedAccount?.address;
  warrior.set_attack = warriorAttackApi?.attack || '';

  const handleShare = async () => {
    let url = '';
    if (lifeAndDeathRound) {
      url = generateHiddenURL(battle, warrior, opponent);
    } else {
      url = `/api/matchup/preview?round=${battle}&warriorOneId=${warrior.warrior_id}&warriorTwoId=${opponent?.warrior_id}`;
    }
    setShareClicked(true);
    const resp = await axios.get(url);
    const imageURL = await resp.data.url;
    setShareURL(imageURL);
    setShareClicked(false);
  };

  return (
    <>
      <div
        className={`battle-matchup ${
          lifeAndDeathRound && 'battle-matchup--life-death'
        }`}
        onMouseEnter={() => setIsActive(true)}
        onMouseLeave={() => setIsActive(false)}
      >
        <div className="battle-matchup__body">
          <Warrior
            facing="east"
            warrior={warrior}
            active={isActive}
            attackPreview={attack}
          />
          {!areWaitingForResults && warrior && isAttackSuccess && (
            <WarriorControls
              warrior={warrior}
              opponent={opponent}
              shareClicked={shareClicked}
              shareURL={shareURL}
              onSelectAttack={(_attack) => setAttack(_attack)}
              onShare={() => {
                shareRef.current?.click();
              }}
            />
          )}
          {/* {arePairngsOnly ? 'Waiting for tournament to begin' : 'Waiting on results'} */}
          {areWaitingForResults && (
            <div className="battle-matchup__revleal-result">
              Waiting on results 🫡
            </div>
          )}
          {opponent && (
            <Warrior
              facing="west"
              warrior={opponent}
              active={isActive}
              lifeAndDeathRoundOpponent={
                lifeAndDeathRound && !opponent.warrior_id
              }
            />
          )}
        </div>
        <a
          className="battle-matchup__share"
          target="_blank"
          ref={shareRef}
          onClick={handleShare}
          rel="noreferrer"
        >
          Show
        </a>
      </div>
      {!areResultsReady && warrior && opponent && (
        <div className={`battle-record__footer battle-record--mobile ${showOwnerRecord || showOpponentRecord ? 'battle-record__column' : ''} `}>
          <BattleRecord warrior={warrior} isOwner={true} showRecord={showOwnerRecord} setShowRecord={setShowOwnerRecord} />
          <BattleRecord warrior={opponent} isOwner={false} showRecord={showOpponentRecord} setShowRecord={setShowOpponentRecord} />
        </div>
      )}
    </>
  );
}
